import { Button } from '@edsn/pie-components';
import editIcon from '@iconify/icons-material-symbols/edit-outline';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  DescriptionList,
  DescriptionListItem,
} from '#pie/components/description-list/DescriptionList';
import { Icon } from '#pie/components/icon/Icon';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useToast } from '#pie/components/toast/ToastContext';
import {
  type CprContactResponse,
  getGetManagementContactQueryKey,
  type UpdateContactRequest,
  useGetManagementContacts,
  useUpdateManagementContact,
} from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';

const { FormText, FormPhone } = typedFormFields<UpdateContactRequest>();

interface PersonalDataSectionProps {
  contactperson: CprContactResponse;
  contactpersonId: string;
}

export const PersonalDataSection = ({
  contactperson,
  contactpersonId,
}: PersonalDataSectionProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { data: contacts } = useGetManagementContacts({
    pagination: { pageIndex: 0, pageSize: 10000 },
    filter: {},
  });
  const formMethods = useForm<UpdateContactRequest>();
  const [editCard, setEditCard] = useState(false);

  const {
    clearErrors,
    getValues,
    reset,
    setError,
    trigger,
    formState: { isDirty },
  } = formMethods;

  const getFormValues = (contactperson: CprContactResponse) => ({
    firstName: contactperson.firstName ?? '',
    lastName: contactperson.lastName ?? '',
    email: contactperson.email ?? '',
    phone: contactperson.phone ?? '',
  });

  const { mutate, isPending } = useUpdateManagementContact({
    mutation: {
      onError: () => {
        addToast({
          message: t(
            'cpr_contact_group_detail.update_user.toast.error.message',
          ),
          title: t('cpr_management.contact_modal.button.updated'),
          type: 'error',
        });
      },
      onSuccess: (_data, variables) => {
        const firstName = variables.data.firstName;
        const lastName = variables.data.lastName;
        const name = `${firstName} ${lastName}`;
        queryClient.invalidateQueries({
          queryKey: ['/cpr-management-contact/contact'],
        });

        queryClient.invalidateQueries({
          queryKey: getGetManagementContactQueryKey(contactpersonId),
        });

        addToast({
          message: t(
            'cpr_contact_group_detail.update_user.toast.success.message',
            { name },
          ),
          title: t('cpr_management.contact_modal.button.updated'),
          type: 'success',
        });
        setEditCard(false);
      },
    },
  });

  const handleSubmit: SubmitHandler<UpdateContactRequest> = async (data) => {
    const valid = await trigger(['firstName', 'lastName', 'email', 'phone']);

    if (!valid) {
      return;
    }

    const email = getValues('email').trim().toLowerCase();
    const currentEmail = contactperson.email?.trim().toLowerCase();
    const emailExists =
      email !== currentEmail &&
      contacts?.items.some(
        (contact) =>
          contact.id !== contactpersonId &&
          contact.email?.trim().toLowerCase() === email,
      );

    if (emailExists) {
      setError('email', {
        type: 'validate',
        message: t('cpr_contact_group_detail.create_user.error.email_exists'),
      });
      return;
    }

    clearErrors('email');

    mutate({
      id: contactpersonId,
      data,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
        <Stack gap="lg" className="mx-auto my-6 max-w-4xl">
          <div className="bg-white rounded-md px-6">
            <div className="flex flex-row justify-between items-center h-14 border-b border-border-minimal-enabled">
              <Text as="h6" variant="h6">
                {t('common.personal_info')}
              </Text>
              {editCard ? (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    action="tertiary"
                    emphasis="minimal"
                    type="button"
                    onClick={() => {
                      reset(getFormValues(contactperson));
                      setEditCard(false);
                    }}
                  >
                    {t('common.button.cancel')}
                  </Button>
                  <Button
                    size="sm"
                    action="tertiary"
                    emphasis="subtle"
                    type="submit"
                    disabled={!isDirty || isPending}
                  >
                    {t('common.save')}
                  </Button>
                </div>
              ) : (
                !isPending && (
                  <Button
                    size="sm"
                    action="tertiary"
                    emphasis="minimal"
                    type="button"
                    onClick={() => {
                      reset(getFormValues(contactperson));
                      setEditCard(true);
                    }}
                  >
                    <Icon
                      icon={editIcon}
                      className="text-action-secondary-enabled shrink-0 group-data-[state=open]:hidden mr-2"
                      size={16}
                      aria-hidden
                    />
                    {t('common.edit')}
                  </Button>
                )
              )}
            </div>
            {editCard ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 pt-4 pb-6">
                <FormText
                  name="firstName"
                  label={t('common.first_name')}
                  fullWidth
                  rules={{ required: true }}
                />
                <FormText
                  name="lastName"
                  label={t('common.last_name_prefix')}
                  fullWidth
                  rules={{ required: true }}
                />
                <FormText
                  name="email"
                  type="email"
                  label={t('common.email')}
                  fullWidth
                  rules={{ required: true }}
                />
                <FormPhone name="phone" rules={{ required: true }} />
              </div>
            ) : (
              <div className="pt-4 pb-6">
                <DescriptionList variant="grid">
                  <DescriptionListItem
                    direction="vertical"
                    title={t('common.first_name')}
                  >
                    {contactperson.firstName}
                  </DescriptionListItem>
                  <DescriptionListItem
                    direction="vertical"
                    title={t('common.last_name_prefix')}
                  >
                    {contactperson.lastName}
                  </DescriptionListItem>
                  <DescriptionListItem
                    direction="vertical"
                    title={t('common.email')}
                  >
                    {contactperson.email}
                  </DescriptionListItem>

                  <DescriptionListItem
                    direction="vertical"
                    title={t('common.phone')}
                  >
                    {contactperson.phone}
                  </DescriptionListItem>
                </DescriptionList>
              </div>
            )}
          </div>
        </Stack>
      </form>
    </FormProvider>
  );
};
